import { useState, useRef, useEffect, FC, ReactNode } from 'react';

interface Props {
  isOpen: boolean;
  children: ReactNode;
  className?: string;
  duration?: number;
  keepMounted?: boolean;
}

const SlideDown: FC<Props> = ({
  isOpen,
  children,
  className,
  duration = 200,
  keepMounted = false,
}) => {
  const [height, setHeight] = useState(0);
  const [isOpened, setOpened] = useState(isOpen);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setOpened(true);
    } else if (!keepMounted || !isOpened) {
      const timeout = setTimeout(() => {
        setShouldRender(false);
      }, duration);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isOpen, duration, keepMounted, isOpened]);

  useEffect(() => {
    if (!contentRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (isOpen) {
          setHeight(entry.contentRect.height);
        }
      }
    });

    if (shouldRender || (keepMounted && isOpened)) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [isOpen, shouldRender, keepMounted, isOpened]);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [isOpen]);

  const isContentRendered = shouldRender || (keepMounted && isOpened);

  return (
    <div
      style={{
        overflow: 'hidden',
        transition: `height ${duration}ms ease-in-out`,
        height: `${height}px`,
      }}>
      {isContentRendered && (
        <div ref={contentRef} className={className}>
          {children}
        </div>
      )}
    </div>
  );
};

export default SlideDown;
